// apps/web/src/pages/settings/restaurant.tsx — Phase 2 plan 02-04 B6·B restaurant profile.
//
// Form fields per the B6·B wireframe (02-UI-SPEC.md):
//   - name, slotDurationMinutes, capacityDefault, holdbackDefault
//   - addressLine, postalCode, city, phone, email, contactNotes
//   - tagline {de, en}, heroCopy {de, en}, aboutText {de, en}
//
// Pitfall 9 (RESEARCH): after a successful `updateRestaurant` mutation,
// call queryClient.invalidateQueries({queryKey: ['restaurant']}) so the
// next read picks up the new value WITHOUT a manual page reload. This
// is the "persistence-after-reload" proof: navigating away and back
// shows the new name.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gqlRequest } from "../../lib/api";
import { RESTAURANT_QUERY, UPDATE_RESTAURANT_MUTATION } from "./operations";

interface TranslPair {
  de: string;
  en: string | null;
}

interface Restaurant {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  slotDurationMinutes: number;
  capacityDefault: number;
  holdbackDefault: number;
  addressLine: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  contactNotes: string | null;
  tagline: TranslPair | null;
  heroCopy: TranslPair | null;
  aboutText: TranslPair | null;
}

interface RestaurantResponse {
  restaurant: Restaurant;
}

interface UpdateRestaurantResponse {
  updateRestaurant: { id: string; name: string };
}

interface FormState {
  name: string;
  slotDurationMinutes: number;
  capacityDefault: number;
  holdbackDefault: number;
  addressLine: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
  contactNotes: string;
  taglineDe: string;
  taglineEn: string;
  heroCopyDe: string;
  heroCopyEn: string;
  aboutTextDe: string;
  aboutTextEn: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  slotDurationMinutes: 30,
  capacityDefault: 40,
  holdbackDefault: 0,
  addressLine: "",
  postalCode: "",
  city: "",
  phone: "",
  email: "",
  contactNotes: "",
  taglineDe: "",
  taglineEn: "",
  heroCopyDe: "",
  heroCopyEn: "",
  aboutTextDe: "",
  aboutTextEn: "",
};

function fromRestaurant(r: Restaurant): FormState {
  return {
    name: r.name,
    slotDurationMinutes: r.slotDurationMinutes,
    capacityDefault: r.capacityDefault,
    holdbackDefault: r.holdbackDefault,
    addressLine: r.addressLine ?? "",
    postalCode: r.postalCode ?? "",
    city: r.city ?? "",
    phone: r.phone ?? "",
    email: r.email ?? "",
    contactNotes: r.contactNotes ?? "",
    taglineDe: r.tagline?.de ?? "",
    taglineEn: r.tagline?.en ?? "",
    heroCopyDe: r.heroCopy?.de ?? "",
    heroCopyEn: r.heroCopy?.en ?? "",
    aboutTextDe: r.aboutText?.de ?? "",
    aboutTextEn: r.aboutText?.en ?? "",
  };
}

export function RestaurantDetailPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const restaurantQuery = useQuery<RestaurantResponse>({
    queryKey: ["settings", "restaurant"],
    queryFn: async () => gqlRequest<RestaurantResponse>(RESTAURANT_QUERY),
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (restaurantQuery.data?.restaurant) {
      setForm(fromRestaurant(restaurantQuery.data.restaurant));
    }
  }, [restaurantQuery.data]);

  const mutation = useMutation({
    mutationFn: async () => {
      const input: Record<string, unknown> = {
        name: form.name,
        slotDurationMinutes: Number(form.slotDurationMinutes),
        capacityDefault: Number(form.capacityDefault),
        holdbackDefault: Number(form.holdbackDefault),
        addressLine: form.addressLine || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
        phone: form.phone || null,
        email: form.email || null,
        contactNotes: form.contactNotes || null,
      };
      if (form.taglineDe) {
        input.tagline = {
          de: form.taglineDe,
          ...(form.taglineEn ? { en: form.taglineEn } : {}),
        };
      }
      if (form.heroCopyDe) {
        input.heroCopy = {
          de: form.heroCopyDe,
          ...(form.heroCopyEn ? { en: form.heroCopyEn } : {}),
        };
      }
      if (form.aboutTextDe) {
        input.aboutText = {
          de: form.aboutTextDe,
          ...(form.aboutTextEn ? { en: form.aboutTextEn } : {}),
        };
      }
      return gqlRequest<UpdateRestaurantResponse>(UPDATE_RESTAURANT_MUTATION, {
        input,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["settings", "restaurant"],
      });
      setToast(t("settings.restaurant.saved"));
      setTimeout(() => setToast(null), 2000);
    },
    onError: () => {
      setToast(t("settings.restaurant.saveError"));
      setTimeout(() => setToast(null), 3000);
    },
  });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <main className="min-h-screen bg-bg text-text font-body">
      <header className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3">
        <Link
          to="/admin/einstellungen"
          className="text-text/70 hover:text-text"
          aria-label={t("settings.hub.backToHub")}
          data-testid="restaurant-back"
        >
          ‹
        </Link>
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="restaurant-title"
        >
          {t("settings.hub.rowHours")}
        </h1>
      </header>

      {restaurantQuery.isLoading && (
        <p className="px-4 py-6 text-text/70" data-testid="restaurant-loading">
          …
        </p>
      )}

      {restaurantQuery.isError && (
        <p className="px-4 py-6 text-text/70" data-testid="restaurant-error">
          {t("settings.restaurant.saveError")}
        </p>
      )}

      {restaurantQuery.data && (
        <form
          className="px-4 py-6 w-full max-w-3xl space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          data-testid="restaurant-form"
        >
          <Field label={t("settings.restaurant.nameLabel")}>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="field w-full"
              data-testid="restaurant-name-input"
              required
            />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field label={t("settings.restaurant.slotDurationLabel")}>
              <input
                type="number"
                min={5}
                max={120}
                value={form.slotDurationMinutes}
                onChange={(e) =>
                  update("slotDurationMinutes", Number(e.target.value))
                }
                className="field w-full"
                data-testid="restaurant-slot-duration"
              />
            </Field>
            <Field label={t("settings.restaurant.capacityLabel")}>
              <input
                type="number"
                min={0}
                value={form.capacityDefault}
                onChange={(e) =>
                  update("capacityDefault", Number(e.target.value))
                }
                className="field w-full"
                data-testid="restaurant-capacity"
              />
            </Field>
            <Field label={t("settings.restaurant.holdbackLabel")}>
              <input
                type="number"
                min={0}
                value={form.holdbackDefault}
                onChange={(e) =>
                  update("holdbackDefault", Number(e.target.value))
                }
                className="field w-full"
                data-testid="restaurant-holdback"
              />
            </Field>
          </div>

          <Field label={t("settings.restaurant.addressLabel")}>
            <input
              type="text"
              value={form.addressLine}
              onChange={(e) => update("addressLine", e.target.value)}
              className="field w-full"
              data-testid="restaurant-address"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("settings.restaurant.postalLabel")}>
              <input
                type="text"
                value={form.postalCode}
                onChange={(e) => update("postalCode", e.target.value)}
                className="field w-full"
                data-testid="restaurant-postal"
              />
            </Field>
            <Field label={t("settings.restaurant.cityLabel")}>
              <input
                type="text"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                className="field w-full"
                data-testid="restaurant-city"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("settings.restaurant.phoneLabel")}>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="field w-full"
                data-testid="restaurant-phone"
              />
            </Field>
            <Field label={t("settings.restaurant.emailLabel")}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="field w-full"
                data-testid="restaurant-email"
              />
            </Field>
          </div>
          <Field label={t("settings.restaurant.contactNotesLabel")}>
            <textarea
              value={form.contactNotes}
              onChange={(e) => update("contactNotes", e.target.value)}
              className="field w-full"
              rows={3}
              data-testid="restaurant-contact-notes"
            />
          </Field>

          <Field label={t("settings.restaurant.taglineLabel")}>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={form.taglineDe}
                onChange={(e) => update("taglineDe", e.target.value)}
                placeholder="DE"
                className="field w-full"
                data-testid="restaurant-tagline-de"
              />
              <input
                type="text"
                value={form.taglineEn}
                onChange={(e) => update("taglineEn", e.target.value)}
                placeholder="EN"
                className="field w-full"
                data-testid="restaurant-tagline-en"
              />
            </div>
          </Field>

          <Field label={t("settings.restaurant.heroCopyLabel")}>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={form.heroCopyDe}
                onChange={(e) => update("heroCopyDe", e.target.value)}
                placeholder="DE"
                className="field w-full"
                data-testid="restaurant-hero-de"
              />
              <input
                type="text"
                value={form.heroCopyEn}
                onChange={(e) => update("heroCopyEn", e.target.value)}
                placeholder="EN"
                className="field w-full"
                data-testid="restaurant-hero-en"
              />
            </div>
          </Field>

          <Field label={t("settings.restaurant.aboutTextLabel")}>
            <div className="grid grid-cols-2 gap-2">
              <textarea
                value={form.aboutTextDe}
                onChange={(e) => update("aboutTextDe", e.target.value)}
                placeholder="DE"
                className="field w-full"
                rows={3}
                data-testid="restaurant-about-de"
              />
              <textarea
                value={form.aboutTextEn}
                onChange={(e) => update("aboutTextEn", e.target.value)}
                placeholder="EN"
                className="field w-full"
                rows={3}
                data-testid="restaurant-about-en"
              />
            </div>
          </Field>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={mutation.isPending}
              data-testid="restaurant-save"
            >
              {t("settings.restaurant.save")}
            </button>
            <Link
              to="/admin/einstellungen"
              className="btn btn-secondary"
              data-testid="restaurant-cancel"
            >
              {t("settings.restaurant.cancel")}
            </Link>
            {toast && (
              <span
                className="text-sm text-text/70"
                data-testid="restaurant-toast"
                role="status"
              >
                {toast}
              </span>
            )}
          </div>
        </form>
      )}
    </main>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-text/70 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
